import type { ApiLocale } from '@noor/api';
import * as z from 'zod';

import { pendingChangeRequestSchema } from './product';

// Image Service — the ONE write surface for every entity's photos, as of the
// 2026-08-14 spec revision. It replaces four unrelated per-entity protocols
// that used to live on the owning service:
//
//   product          POST/PUT/DELETE products/{id}/images*  + set-main
//   restaurant       POST/DELETE restaurants/{id}/image[/{slot}]
//   restaurant_banner|product_banner
//                    POST/DELETE {…}-banners/{id}/image[/{locale}]
//
// All of those routes are GONE from `openapi.json`, not deprecated — calling
// one now 404s, so this was a hard cut-over rather than an optimization.
//
// Two endpoints replace them:
//
//   PUT    images/{entity_type}/{parent_id}              upsert up to 10 photos
//   DELETE images/{entity_type}/{parent_id}/{selector}   remove one photo
//
// READS are untouched: a product still carries `images[]`, a restaurant its
// `icon`/`background_image`, a banner its resolved `image`. Only writes moved.
//
// `link` is still a freshly-uploaded storage KEY, never a display URL — reads
// expand keys one-way, so a stored photo can never be written back (see the
// app's CLAUDE.md #13).

/**
 * Which kind of entity `parent_id` addresses. The four are NOT
 * interchangeable: each accepts a different way of naming a photo (see
 * {@link IMAGE_TYPES} vs `locale`), and the two banner types differ only in
 * which service owns the parent row.
 */
export const IMAGE_ENTITY_TYPES = {
  product: 'product',
  restaurant: 'restaurant',
  restaurantBanner: 'restaurant_banner',
  productBanner: 'product_banner',
} as const;
export type ImageEntityType =
  (typeof IMAGE_ENTITY_TYPES)[keyof typeof IMAGE_ENTITY_TYPES];

/**
 * `v1ImageType` — which photo slot an item addresses, for the two entity types
 * that have a real slot concept. Banners have NO `image_type` column at all and
 * select by `locale` instead, so the field is left unset for them.
 *
 * - `MAIN` — product: exactly one per product, ever (the cover)
 * - `COMMON` — product: many allowed; addressed by `image_id`, not by type alone
 * - `ICON` / `BACKGROUND` — restaurant: exactly one each
 *
 * Sent UPPERCASE in the PUT body, but the DELETE path wants the lowercase
 * selector — {@link imageSelector} is the bridge, so no call site hand-cases it.
 */
export const IMAGE_TYPES = {
  main: 'MAIN',
  common: 'COMMON',
  icon: 'ICON',
  background: 'BACKGROUND',
} as const;
export type ImageType = (typeof IMAGE_TYPES)[keyof typeof IMAGE_TYPES];

/**
 * The `{selector}` path segment of the DELETE — which photo to remove. It is
 * documented case-INSENSITIVE, but is sent lowercase to match the spec's own
 * spelling (`"main"`, `"icon"`, `"ru"`).
 *
 * Takes either an {@link ImageType} (which the PUT body spells UPPERCASE, so
 * it needs lowering) or a banner's already-lowercase `ApiLocale` — one
 * conversion so no call site hand-cases the difference.
 */
export function imageSelector(selector: ImageType | ApiLocale): string {
  return selector.toLowerCase();
}

/**
 * One photo to write. Exactly which members apply depends on the entity type,
 * and the gateway rejects unknown fields rather than ignoring them:
 *
 * | entity_type                     | names the photo by | `link`                 |
 * | ------------------------------- | ------------------ | ---------------------- |
 * | `product`                       | `image_type` (+ `image_id`) | required, except on a promote |
 * | `restaurant`                    | `image_type`       | required               |
 * | `restaurant_banner`/`product_banner` | `locale`      | required               |
 *
 * `image_id` carries two distinct meanings on a product, both of which the
 * batch relies on:
 *
 * - with `COMMON` — targets ONE of the product's many common photos. Absent
 *   adds a new photo; present replaces that specific one in place.
 * - with `MAIN` and NO `link` — promotes an existing photo to cover, demoting
 *   whichever holds it now. A pure role swap: nothing about the file changes,
 *   which is what makes a cover change possible at all given that a stored
 *   photo's key is unrecoverable.
 */
export interface UpsertImageItem {
  /** Omitted for banners, which have no image-type column. */
  image_type?: ImageType;
  /** Banners only — `ru` | `uz` | `en`. */
  locale?: string;
  /** Product only; see the two meanings above. */
  image_id?: string;
  /** The storage key. Omitted ONLY on a promote-to-main. */
  link?: string;
}

/**
 * The most photos one PUT accepts. The spec does not state a cap, but the
 * presign batch it is always paired with does (`PRESIGN_MAX_BATCH`), so a
 * larger gallery is already uploaded in chunks — this keeps the write chunked
 * the same way rather than betting on an unbounded body.
 */
export const UPSERT_IMAGES_MAX_BATCH = 10;

/**
 * `v1UpsertImageItemResult` — one row of the PUT's `list`. Every field is
 * optional on the wire (protobuf-JSON omits zero values), hence the defaults.
 *
 * Rows are NOT a mirror of the request, in two ways that have to be handled:
 *
 * - `image_type` is the type ACTUALLY written, not an echo. A product with no
 *   photos promotes its very first one to `MAIN` even when `COMMON` was asked
 *   for, so a caller must not assume row N matches request item N's type.
 * - when `pending_change` is set NOTHING was written yet: `path` is empty,
 *   `image_id` may be absent, and `image_type` is only the queued intent.
 */
export const upsertImageItemResultSchema = z.object({
  /**
   * Populated for product MAIN/COMMON writes that resolve to a concrete photo.
   * ABSENT for banners and for a restaurant's fixed icon/background slots —
   * those have no addressable image row, so there is no id to return.
   */
  image_id: z.string().default(''),
  image_type: z.string().default(''),
  /** The link that was written; empty on a gated write or a pure promote. */
  path: z.string().default(''),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type UpsertImageItemResult = z.infer<typeof upsertImageItemResultSchema>;

/**
 * `v1ImageMutationResponse` — the DELETE's `data`. Carries only a receipt: a
 * gated delete is a SUCCESS with nothing removed yet, and reporting it as done
 * would tell the merchant a photo is gone while a moderator still has to
 * approve it (see the app's CLAUDE.md #14).
 */
export const imageMutationResponseSchema = z.object({
  path: z.string().default(''),
  pending_change: pendingChangeRequestSchema.optional(),
});
export type ImageMutationResult = z.infer<typeof imageMutationResponseSchema>;
